/**
 * E2E renderer probe for the permissions grant terminals.
 *
 * WHY: permissions_grant_succeeded / permissions_grant_failed must be receipted
 * at PostHog's send boundary. The OS permission dialog is not reachable in the
 * desktop lane, so this calls the same tracker the modal calls, behind the same
 * track(). Mirrors onboarding-telemetry-probe.ts.
 * Per rule: stay-in-scope + investigate-before-adding (ai.knowledge/global).
 */
import { EVENT_CATEGORIES } from '#~/analytics/constants/index.js';
import { createPermissionsLifecycleTracker } from '#~/analytics/domains/permissions-lifecycle.js';
import type { AnalyticsTrackFn } from '#~/analytics/domains/types.js';
import type { EventCategory } from '#~/analytics/constants/event-categories.js';
import type { EventProperties } from '#~/analytics/types/properties.js';
import { track as trackAnalyticsEvent } from '#~/analytics/track.js';

interface PermissionsTelemetryProbe {
  grantSucceeded(surface: string, modalInstanceId: string): void;
  grantFailed(surface: string, modalInstanceId: string, errorMessage: string): void;
}

const isE2eEnabled = (): boolean => {
  try {
    return (
      (globalThis as { window?: { electron?: { process?: { env?: Record<string, string> } } } })
        .window?.electron?.process?.env?.IRIS_E2E_TEST === '1'
    );
  } catch {
    return false;
  }
};

export function sendPermissionsProbeEvent(
  name: string,
  category: EventCategory,
  properties?: EventProperties
): void {
  if (category !== EVENT_CATEGORIES.ONBOARDING) return;
  void trackAnalyticsEvent({
    name,
    category,
    ...(properties !== undefined ? { properties } : {}),
    timestamp: Date.now()
  });
}

const trackPermissions: AnalyticsTrackFn = (name, category, properties) => {
  sendPermissionsProbeEvent(name, category, properties);
};

if (isE2eEnabled()) {
  const tracker = createPermissionsLifecycleTracker(trackPermissions);
  const probe: PermissionsTelemetryProbe = {
    grantSucceeded(surface, modalInstanceId) {
      tracker.trackGrantSucceeded({ surface, modal_instance_id: modalInstanceId });
    },
    grantFailed(surface, modalInstanceId, errorMessage) {
      tracker.trackError(
        { surface, modal_instance_id: modalInstanceId },
        { permission_key: 'camera', error_message: errorMessage }
      );
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (globalThis as any).__irisPermissionsTelemetry = probe;
}
